import React, {useCallback, useState} from 'react';
import {createPortal} from 'react-dom';
import {useEffect, useRef} from 'react';
import {CSSProperties} from 'styled-components';

//hooks
import useLockHTMLScroll from '@stencil/lock-body-scroll-hook';
import {useDelayUnmount} from '../../hooks/useDelayUnmount';
import {useModalBackNavigation} from '../../hooks/useModalBackNavigation';

//Components
import InfoModalLoader from './InfoModalLoader';
import {CustomContainerStyles, ModalStyleType} from '@stencil/hotels-types/src/hotels-types';

//Utils
import {mergeStyles, styleMap} from './InfoModalUtils';

//styles
import * as S from './infoModal.styles';

interface InfoModalProps {
  children: React.ReactNode;
  customContainerStyles?: CustomContainerStyles;
  customModalKey: string;
  customRightKey?: React.ReactNode;
  description?: string;
  header?: string;
  isLoading?: boolean;
  isFullScreen?: boolean;
  isLeftCloseButton?: boolean;
  isModalOpen: boolean;
  isSSR?: boolean;
  styleType?: ModalStyleType;
  onModalChange: (isOpen: boolean) => void;
}

interface RenderModalElementProps extends InfoModalProps {
  customClose?: (...args) => void;
  delayModalUnmount?: boolean;
  shouldModalRender?: boolean;
}

interface ModalHeaderProps {
  header?: string;
  description?: string;
  customContainerStyles?: {
    header?: React.CSSProperties;
    headerTag?: React.CSSProperties;
    headerText?: React.CSSProperties;
    descText?: React.CSSProperties;
    close?: React.CSSProperties;
    closeBeforeAfterStyles?: CSSProperties;
  };
  isLeftCloseButton?: boolean;
  customClose?: () => void;
  customRightKey?: React.ReactNode;
  styleType?: ModalStyleType;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({
  header,
  description,
  customContainerStyles,
  isLeftCloseButton,
  customClose,
  customRightKey,
  styleType,
}) => {
  if (!(header || description)) return null;
  return (
    <>
      {(header || description) && (
        <S.ModalHeader style={{...customContainerStyles?.header}}>
          <S.ModalLeftWrapper>
            {isLeftCloseButton && (
              <S.LeftButton onClick={customClose} style={{...customContainerStyles?.close}} />
            )}
            <div>
              {header && (
                <p className="headerText" style={{...customContainerStyles?.headerText}}>
                  {header}
                </p>
              )}
              {description && (
                <p className="descText" style={{...customContainerStyles?.descText}}>
                  {description}
                </p>
              )}
            </div>
          </S.ModalLeftWrapper>
          {customRightKey ? (
            <>{customRightKey}</>
          ) : (
            !isLeftCloseButton && (
              <S.CloseBtn
                onClick={customClose}
                style={{...customContainerStyles?.close}}
                $closeBeforeAfterStyles={{...customContainerStyles?.closeBeforeAfterStyles}}
              ></S.CloseBtn>
            )
          )}
        </S.ModalHeader>
      )}
    </>
  );
};

const RenderModalElement = (props: RenderModalElementProps) => {
  const {
    children,
    customRightKey,
    description = '',
    header = '',
    isLoading = false,
    isFullScreen = false,
    isLeftCloseButton = false,
    isModalOpen,
    styleType = ModalStyleType.Default, // Default style type
    customContainerStyles: propCustomContainerStyles = {},
    customClose,
    isSSR = false,
    delayModalUnmount,
    shouldModalRender = false,
  } = props || {};

  const selectedStyles = styleMap[styleType];

  const customContainerStyles = mergeStyles(
    selectedStyles.customContainerStyles,
    propCustomContainerStyles,
  );

  useLockHTMLScroll({
    stateOnWhichLockToggles: isModalOpen,
  });

  const visibility = isSSR && !shouldModalRender;

  return (
    <>
      <S.ModalContainer
        className={`${!isModalOpen ? 'isClosed' : ''} ${visibility ? 'isHidden' : ''}`}
        $isFullScreen={isFullScreen}
        style={{...customContainerStyles?.container}}
        animation={customContainerStyles?.customAnimation?.animationVal}
      >
        <S.ModalInnerContainer style={{...customContainerStyles?.innerContainer}}>
          <S.ModalBodyWrapper style={{...customContainerStyles?.contentWrapper}}>
            {isLoading ? (
              InfoModalLoader({
                children,
                title: header,
                isFullScreen,
                customContentWrapperStyles: customContainerStyles?.contentWrapper,
                customClose,
              })
            ) : (
              <>
                <ModalHeader
                  header={header}
                  description={description}
                  customContainerStyles={customContainerStyles}
                  isLeftCloseButton={isLeftCloseButton}
                  customClose={customClose}
                  customRightKey={customRightKey}
                  styleType={styleType}
                />
                <S.ModalContentWrapper
                  $isFullScreen={isFullScreen}
                  style={{...customContainerStyles?.content}}
                >
                  {children}
                </S.ModalContentWrapper>
              </>
            )}
          </S.ModalBodyWrapper>
        </S.ModalInnerContainer>
      </S.ModalContainer>
      {delayModalUnmount && <S.ModalBackdrop onClick={customClose}></S.ModalBackdrop>}
    </>
  );
};

const InfoModal = (props: InfoModalProps) => {
  const {isSSR = false, customModalKey, isModalOpen, onModalChange} = props || {};
  const modalRoot = useRef<HTMLElement | null>(null);
  // will delay the unmount of base ModalBackdrop
  const [delayModalUnmount, setDelayModalUnmount] = useState(isModalOpen);

  useEffect(() => {
    // Find the root element in your DOM
    let modalRootDiv = document.getElementById('modal-root');
    // If there is no root then create one
    if (!modalRootDiv) {
      const tempEl = document.createElement('div');
      tempEl.id = 'modal-root';
      document.body.append(tempEl);
      modalRootDiv = tempEl;
    }
    modalRoot.current = modalRootDiv;
    if (isModalOpen) setDelayModalUnmount(isModalOpen);
  }, [isModalOpen]);

  const {customClose} = useModalBackNavigation({
    isModalOpen,
    callback: () => {
      onModalChange(false);
      setTimeout(() => {
        setDelayModalUnmount(isModalOpen);
      }, 200);
    },
    customModalKey,
  });

  const shouldModalRender = useDelayUnmount(isModalOpen, 200);

  const renderModalElement = useCallback(() => {
    if (shouldModalRender) {
      return (
        <RenderModalElement
          {...props}
          delayModalUnmount={delayModalUnmount}
          customClose={customClose}
        />
      );
    }
    return null;
  }, [shouldModalRender, customClose, props]);

  if (isSSR) {
    return (
      <RenderModalElement
        {...props}
        delayModalUnmount={delayModalUnmount}
        shouldModalRender={shouldModalRender}
        customClose={customClose}
      />
    );
  }

  if (modalRoot.current) {
    return createPortal(renderModalElement(), modalRoot.current);
  }

  return null;
};

export default InfoModal;
